import { useEffect, useRef } from 'react';
import { List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectViewerIsInternal } from 'flow/selectors/meeting-selectors';
import { selectWaitingParticipants } from 'flow/selectors/video-selectors';
import { playWaitingParticipantAlert } from 'flow/utils/meeting-sounds';

/**
 * Sounds an alert when someone new arrives at the meeting's door (VID-76).
 *
 * Signaling broadcasts the whole waiting list on every change, so an arrival
 * has to be worked out by comparing against the last list rather than read off
 * the message: a name we have not seen before is someone who just knocked,
 * while a list that only shrank is somebody being let in or giving up.
 *
 * The first list is taken as a baseline and passes in silence. People already
 * waiting when we arrive did not just knock — the header pill shows them
 * immediately — and treating them as arrivals would sound the alert on every
 * reconnect.
 *
 * Only internal participants are alerted, matching who is shown the queue at
 * all. The baseline is kept regardless, because identity resolves after the
 * join: without it, a guest reclassified as a colleague mid-meeting would hear
 * the whole standing queue announced at once.
 */
const useWaitingParticipantAlert = (): void => {
    const viewerIsInternal = useAppSelector(selectViewerIsInternal);
    const waitingParticipants: List<string> = useAppSelector(
        selectWaitingParticipants,
    );

    const previousWaitingNames = useRef<Set<string> | null>(null);

    useEffect(() => {
        const waitingNames = new Set(waitingParticipants.toArray());
        const previous = previousWaitingNames.current;
        previousWaitingNames.current = waitingNames;

        if (previous === null || !viewerIsInternal) {
            return;
        }

        const someoneArrived = waitingParticipants.some(
            (name) => !previous.has(name),
        );

        // One alert per broadcast, however many arrived in it: several people
        // knocking at once is one thing to react to, and a single element
        // cannot play over itself anyway.
        if (someoneArrived) {
            playWaitingParticipantAlert();
        }
    }, [waitingParticipants, viewerIsInternal]);
};

export default useWaitingParticipantAlert;
